import { useCallback } from 'react';
import useSWR, { type SWRConfiguration, useSWRConfig } from 'swr';

import {
  cfiFindingsPartialUpdate,
  cfiReportsFindingsCreate,
  cfiReportsPartialUpdate,
  cfiReportsPublishCreate,
  cfiReportsReopenCreate,
} from '~/data/generated/endpoints/cfi/cfi';
import {
  contractsCfiReportsList,
  contractsCfiRunCreate,
} from '~/data/generated/endpoints/contracts/contracts';
import { LiftAPIClientError } from '~/data/liftApiMutator';

export type CfiRating = 'favorable' | 'neutral' | 'unfavorable' | 'not_applicable';

export type CfiStatus =
  | 'pending'
  | 'running'
  | 'failed'
  | 'awaiting_review'
  | 'published'
  | 'superseded';

// The backend uses `djangorestframework-camel-case`, so all response payloads
// and request bodies travel as camelCase. The serializer-side field names
// (snake_case in `services/api/main/views/api/serializers/cfi.py`) are
// auto-converted at the parser/renderer boundary.
type CfiFindingSource = 'core' | 'ai_discovered' | 'consultant_custom';

export type CfiFinding = {
  id: string;
  provisionId: string;
  provisionLabel: string;
  source: CfiFindingSource;
  consultantAddedBy: string | null;
  consultantAddedByName: string;
  sortOrder: number;
  // Null for consultant-custom findings — the agent never scored them. Core and
  // AI-discovered findings always carry an agent rating/confidence.
  agentRating: CfiRating | null;
  agentRationale: string;
  // Short, report-facing one-liner (the full agentRationale is the detail).
  agentSummary: string;
  // Owner-facing fix for below-market findings (action-list card). Blank for
  // favorable/neutral findings.
  agentRecommendedAction: string;
  agentConfidence: string | null;
  agentCitedChunkIds: string[];
  agentCitedRuleIds: string[];
  agentCitedFieldPaths: string[];
  agentQualifyingLanguage: Array<{ quote: string; chunkId: string }>;
  agentRetrievedPrecedentIds: string[];
  agentPrecedentDisagreement: boolean;
  agentPrecedentDisagreementExplanation: string;
  consultantRating: CfiRating | null;
  consultantRationale: string;
  consultantSummary: string;
  consultantRecommendedAction: string;
  consultantEditedAt: string | null;
  agreedWithAgent: boolean | null;
  excludedFromReport: boolean;
  finalRating: CfiRating;
  finalRationale: string;
  finalSummary: string;
  finalRecommendedAction: string;
};

export type CfiReport = {
  id: string;
  contract: string;
  rubricVersion: string;
  agentModelVersion: string;
  mode: 'consultant_review' | 'agent_direct';
  status: CfiStatus;
  triggeredByUser: string | null;
  previousPublishedAt: string | null;
  previousPublishedByUser: string | null;
  failureReason: string;
  aggregate: Record<string, number>;
  agentScopeSummary: string;
  consultantScopeSummary: string;
  finalScopeSummary: string;
  agentNextSteps: string;
  consultantNextSteps: string;
  finalNextSteps: string;
  publishedAt: string | null;
  publishedByUser: string | null;
  publishedAttachment: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  findings: CfiFinding[];
};

const LIST_KEY = (contractId: string) => ['cfi-reports', contractId] as const;
const REPORT_KEY = (contractId: string, reportId: string) =>
  ['cfi-report', contractId, reportId] as const;
// Mirror `useContract`'s SWR key (`['contract', contractId]`) so publish/reopen,
// which mutate the contract's attachments, revalidate the open contract detail.
// SWR matches array keys structurally, so `['contract']` (the list key) would
// NOT refresh the single-contract view.
const CONTRACT_KEY = (contractId: string) => ['contract', contractId] as const;

const POLLING_STATUSES: CfiStatus[] = ['pending', 'running'];

const reportsFetcher = async ([_, contractId]: readonly [string, string]) =>
  (await contractsCfiReportsList(contractId)).data as unknown as CfiReport[];

/** Returns the latest CFI report for a contract, polling every 3s while
 * pending/running so the UI transitions automatically when scoring completes.
 */
export const useLatestCfiReport = (contractId: string | null | undefined) => {
  const swr = useSWR(contractId ? LIST_KEY(contractId) : null, reportsFetcher, {
    refreshInterval: (data?: CfiReport[]) => {
      const latest = data?.[0];
      if (!latest) return 0;
      return POLLING_STATUSES.includes(latest.status) ? 3000 : 0;
    },
  } as SWRConfiguration);

  return {
    latest: (swr.data?.[0] ?? null) as CfiReport | null,
    reports: swr.data ?? [],
    error: swr.error,
    isLoading: swr.isLoading,
    mutate: swr.mutate,
  };
};

export const useCfiMutations = (contractId: string | null | undefined) => {
  const { mutate: globalMutate } = useSWRConfig();

  const run = useCallback(async () => {
    if (!contractId) return;
    try {
      await contractsCfiRunCreate(contractId);
    } catch (err) {
      // 409 = a run is already in progress, or a report is awaiting review, for
      // this contract. That's benign (e.g. a double-click): revalidate so the UI
      // adopts the existing report instead of surfacing an unhandled rejection.
      if (err instanceof LiftAPIClientError && err.context.status === 409) {
        await globalMutate(LIST_KEY(contractId));
        return;
      }
      throw err;
    }
    await globalMutate(LIST_KEY(contractId));
  }, [contractId, globalMutate]);

  const patchFinding = useCallback(
    async (
      finding: CfiFinding,
      body: Partial<{
        consultantRating: CfiRating | null;
        consultantSummary: string;
        consultantRecommendedAction: string;
        agreedWithAgent: boolean | null;
        excludedFromReport: boolean;
      }>,
    ) => {
      const updated = (await cfiFindingsPartialUpdate(finding.id, body))
        .data as unknown as CfiFinding;
      if (contractId) {
        await globalMutate(LIST_KEY(contractId));
      }
      return updated;
    },
    [contractId, globalMutate],
  );

  const reopen = useCallback(
    async (reportId: string) => {
      const updated = (await cfiReportsReopenCreate(reportId)).data as unknown as CfiReport;
      if (contractId) {
        await globalMutate(LIST_KEY(contractId));
        await globalMutate(REPORT_KEY(contractId, reportId));
        await globalMutate(CONTRACT_KEY(contractId));
      }
      return updated;
    },
    [contractId, globalMutate],
  );

  const patchReport = useCallback(
    async (reportId: string, body: Partial<{ consultantScopeSummary: string }>) => {
      const updated = (await cfiReportsPartialUpdate(reportId, body)).data as unknown as CfiReport;
      if (contractId) {
        await globalMutate(LIST_KEY(contractId));
      }
      return updated;
    },
    [contractId, globalMutate],
  );

  const publish = useCallback(
    async (reportId: string) => {
      const updated = (await cfiReportsPublishCreate(reportId)).data as unknown as CfiReport;
      if (contractId) {
        await globalMutate(LIST_KEY(contractId));
        await globalMutate(REPORT_KEY(contractId, reportId));
        await globalMutate(CONTRACT_KEY(contractId));
      }
      return updated;
    },
    [contractId, globalMutate],
  );

  const createCustomFinding = useCallback(
    async (
      reportId: string,
      body: {
        provisionLabel: string;
        rating: CfiRating;
        rationale: string;
      },
    ) => {
      const created = (await cfiReportsFindingsCreate(reportId, body))
        .data as unknown as CfiFinding;
      if (contractId) {
        await globalMutate(LIST_KEY(contractId));
      }
      return created;
    },
    [contractId, globalMutate],
  );

  return { run, patchFinding, patchReport, publish, reopen, createCustomFinding };
};
